/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { cn } from '@/lib/utils';
import { layoutNavGroups } from '@/lib/navigation/columns';
import type { NavCategory } from '@/lib/navigation/types';
import { SubnavGroup } from '@/components/navigation/subnav-group';

/**
 * The category area shared by both subnav templates: grouping categories laid out into
 * rows of `columns`, an Across group taking the rest of its row (SCO-NAV-05).
 */
export function SubnavGroups({
    groups,
    columns,
    packStandardGroups,
    className,
    onNavigate,
}: {
    groups: NavCategory[];
    columns: number;
    /** Small gives each group its own row; the desktop panels pack them (SCO-NAV-05). */
    packStandardGroups?: boolean;
    className?: string;
    onNavigate?: () => void;
}): ReactElement {
    const rows = layoutNavGroups(groups, { columns, packStandardGroups });
    const rowSpans = rows.map((row) => row.groups.reduce((total, placed) => total + placed.span, 0));

    // Packed rows share the widest row's grid so a short last row keeps the columns above it
    // (SCO-395), while a panel that never fills a row still spreads — three groups at a third
    // each. Unpacked (small) rows are independent: a standard group there owns its full width.
    const packedTracks = Math.max(1, ...rowSpans);

    return (
        <div className={cn('flex flex-col gap-10', className)}>
            {rows.map((row, index) => {
                const tracks = packStandardGroups === false ? rowSpans[index] : packedTracks;

                return (
                    <div
                        key={row.groups[0].group.id}
                        className="grid items-start gap-8"
                        style={{ gridTemplateColumns: `repeat(${tracks}, minmax(0, 1fr))` }}>
                        {row.groups.map((placed) => (
                            <SubnavGroup key={placed.group.id} placed={placed} onNavigate={onNavigate} />
                        ))}
                    </div>
                );
            })}
        </div>
    );
}
